import { useCallback, useState } from 'react';

// Call records are reviewed against US callers' local time by default; staff
// can switch zones on the page and the choice is remembered in this browser.
export const DEFAULT_CALL_TIMEZONE = 'America/Los_Angeles';
const STORAGE_KEY = 'aiker.callRecordsTimezone';

export const CALL_TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', en: 'US Pacific (Los Angeles)', zh: '美国太平洋时间（洛杉矶）' },
  { value: 'America/Denver', en: 'US Mountain (Denver)', zh: '美国山地时间（丹佛）' },
  { value: 'America/Phoenix', en: 'US Arizona (Phoenix)', zh: '美国亚利桑那（凤凰城）' },
  { value: 'America/Chicago', en: 'US Central (Chicago)', zh: '美国中部时间（芝加哥）' },
  { value: 'America/New_York', en: 'US Eastern (New York)', zh: '美国东部时间（纽约）' },
  { value: 'America/Anchorage', en: 'US Alaska (Anchorage)', zh: '美国阿拉斯加（安克雷奇）' },
  { value: 'Pacific/Honolulu', en: 'US Hawaii (Honolulu)', zh: '美国夏威夷（檀香山）' },
  { value: 'Asia/Shanghai', en: 'Beijing', zh: '北京时间' },
  { value: 'Asia/Hong_Kong', en: 'Hong Kong', zh: '香港时间' },
  { value: 'UTC', en: 'UTC', zh: 'UTC' },
];
const KNOWN = new Set(CALL_TIMEZONE_OPTIONS.map((option) => option.value));

function readStored() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return KNOWN.has(value) ? value : null;
  } catch {
    return null;
  }
}

export function useCallTimezone() {
  const [timezone, setTimezoneState] = useState(() => readStored() || DEFAULT_CALL_TIMEZONE);
  const setTimezone = useCallback((value) => {
    if (!KNOWN.has(value)) return;
    setTimezoneState(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Private windows may block storage; the choice then lasts for this page only.
    }
  }, []);
  return [timezone, setTimezone];
}

// "9/23/2026, 10:15 AM PDT"; a 4-digit year keeps day-first locales from
// reading "26/9/23" as a 2023 date.
export function formatCallTime(value, timezone, options = {}) {
  if (value == null || value === '') return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit',
      timeZone: timezone, timeZoneName: 'short', ...options,
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

function wallParts(date, timeZone) {
  const out = {};
  for (const { type, value } of new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(date)) out[type] = value;
  return out;
}

// "2026-09-23" -> the instant that day starts in timeZone (DST-safe).
// With nextDay=true, the instant the following day starts (exclusive end).
export function zonedDayStartIso(dateString, timeZone, nextDay = false) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString || '');
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const wall = Date.UTC(y, m - 1, d + (nextDay ? 1 : 0), 0, 0);
  let guess = wall;
  for (let i = 0; i < 2; i += 1) {
    const p = wallParts(new Date(guess), timeZone);
    guess += wall - Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute);
  }
  return new Date(guess).toISOString();
}
